import React from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import TimerIcon from '@mui/icons-material/Timer';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface AccountStatusProps {
  status: 'suspended' | 'banned' | null;
  reason?: string;
  endDate?: Date;
}

export default function AccountStatusPage({ status, reason, endDate }: AccountStatusProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  // If no status is provided, redirect to home
  if (!status) {
    navigate('/', { replace: true });
    return null;
  }

  const handleReturnHome = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      // Still navigate even if sign out fails
      navigate('/', { replace: true });
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          sx={{
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'background.paper',
            borderTop: 6,
            borderColor: status === 'banned' ? 'error.main' : 'warning.main'
          }}
        >
          {status === 'banned' ? (
            <BlockIcon 
              sx={{ 
                fontSize: 64, 
                color: 'error.main',
                mb: 2 
              }} 
            />
          ) : (
            <TimerIcon 
              sx={{ 
                fontSize: 64, 
                color: 'warning.main',
                mb: 2 
              }} 
            />
          )}

          <Typography component="h1" variant="h4" gutterBottom align="center">
            {status === 'banned' ? 'Account Banned' : 'Account Suspended'}
          </Typography>

          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {status === 'banned' ? (
              'Your account has been permanently banned from Battleground State.'
            ) : (
              'Your account has been temporarily suspended from Battleground State.'
            )}
          </Typography>

          {reason && (
            <Box sx={{ mb: 3, width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Reason:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {reason}
              </Typography>
            </Box>
          )}

          {status === 'suspended' && endDate && (
            <Box sx={{ mb: 3, width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Suspension Ends:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {endDate.toLocaleDateString()} at {endDate.toLocaleTimeString()}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2, width: '100%' }}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              {status === 'banned' ? (
                'If you believe this was a mistake, please contact our support team.'
              ) : (
                'Your account access will be automatically restored when the suspension period ends.'
              )}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleReturnHome}
              sx={{ mt: 2 }}
            >
              Return to Home
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
} 